/**
 * Calculates an updated highlight range that could be shifted, extended,
 * shrunk, and/or removed according to the change made
 *
 * @param netCharDiff The difference between the original text length and the new text length
 * @param changeStartIdx The index where the change took place.
 *  - For any additive change or non-collapsed removal, this is the start of the selection
 *  - For a collapsed removal, this is the start less the amount of characters removed
 *
 * @returns Original reference to highlights if nothing needs to
 *  be changed, else a new reference to a list of updated highlights
 */
export function getUpdatedHighlightRanges(
    netCharDiff: number,
    changeStartIdx: number,
    highlights: Array<[number, number]>,
): Array<[number, number]> {
    const isNetAddition = netCharDiff > 0;
    const updatedHighlights: Array<[number, number]> = [];

    let hasChanged = false;
    highlights.forEach((highlightRange) => {
        const [startIdx, endIdx] = highlightRange;

        // If there is a net addition, and the change occured within a range or at the very end (just outside)
        // the range, extend it by the number of inserted characters
        const isAdditionWithinRange =
            isNetAddition && changeStartIdx > startIdx && changeStartIdx <= endIdx;
        if (isAdditionWithinRange) {
            hasChanged = true;
            updatedHighlights.push([startIdx, endIdx + netCharDiff]);
            return;
        }

        // Handle net subtractions that affects a highlight range by shrinking it
        const endAffectedIdx = changeStartIdx + Math.abs(netCharDiff);
        const doesSubtractionAffectRange =
            !isNetAddition && endAffectedIdx > startIdx && changeStartIdx <= endIdx;
        if (doesSubtractionAffectRange) {
            hasChanged = true;

            const numCharsDeletedBeforeRange = Math.max(0, startIdx - changeStartIdx);
            const numCharsDeletedInRange =
                Math.min(endAffectedIdx, endIdx) - Math.max(startIdx, changeStartIdx);

            const newStartHighlightIdx = startIdx - numCharsDeletedBeforeRange;
            const newEndHighlightIdx = endIdx - numCharsDeletedBeforeRange - numCharsDeletedInRange;

            // Only preserve the highlight if it wasn't all deleted
            if (newEndHighlightIdx >= newStartHighlightIdx) {
                updatedHighlights.push([newStartHighlightIdx, newEndHighlightIdx]);
            }

            return;
        }

        // For any range that exists before the net changes, adjust the indices accordingly
        const isAdditionBeforeRange = isNetAddition && changeStartIdx <= startIdx;
        const isSubtractionBeforeRange = !isNetAddition && endAffectedIdx <= startIdx;
        if (isAdditionBeforeRange || isSubtractionBeforeRange) {
            hasChanged = true;
            updatedHighlights.push([startIdx + netCharDiff, endIdx + netCharDiff]);
            return;
        }

        // Default case: range exists after net changes. There is no need to adjust
        updatedHighlights.push(highlightRange);
    });

    return hasChanged ? updatedHighlights : highlights;
}

/**
 * Strips away any emojis or other breaking special characters
 *
 * @param text plain text value
 */
export function sanitizeText(text: string): string {
    const noEmojis = text.replace(/(?!©|™|®|★|☆)\p{Extended_Pictographic}/gu, '');
    // oxlint-disable-next-line no-control-regex -- intentionally strips control characters
    return noEmojis.replace(/\u200B|\r|[\b]|\f|\0|\v/g, '');
}
